import moment from "moment";
import SelectModelSearchInputComponent from "../../../components/model/select-model-search-input.component";
import MultiSelectComponent from "../../../components/multi-select/multi-select.component";
import DynamicDateComponent from "../../../components/report/dynamic-date.component";
import { DATE_TYPE_CODES, formats } from "../../../config/constants";
import SellInInvoiceVsImeiScanCountModel from "../../../models/reports/sell-in-invoice-vs-imei-scan-count.model";
import ViewBase from "../../view-base.view";
import SellInInvoiceVsScanCountTableView from "./sell-in-invoice-vs-imei-scan-count-table.view";

export default function SellInInvoiceVsScanCountView() {
  ViewBase.call(this);

  let duration = {
    type: DATE_TYPE_CODES.DATE,
    from: moment().format(formats.dateFormat),
    to: moment().format(formats.dateFormat),
  };

  let searchResult;

  let isDownloadingRawData = false;
  let isDownloadingSummary = false;

  let divisions = [];
  let selectedDivisions = [];

  let selectedModels = [];

  let doNumber = "";

  let prevSearchCache = {
    searchResult,
    duration,
    selectedDivisions,
    selectedModels,
    doNumber,

    updateCache() {
      this.searchResult = searchResult;
      this.duration = duration;
      this.selectedDivisions = selectedDivisions;
      this.selectedModels = selectedModels;
      this.doNumber = doNumber;
    }
  }

  const handleSelectModels = (models) => {
    selectedModels = models;
  }

  const handleClickDownloadSummary = (e) => {
    e.preventDefault();
    isDownloadingSummary = true;

    const convertedDuration = convertDuration(prevSearchCache.duration);

    const config = {
      dateType: convertedDuration.type,
      fromDate: convertedDuration.from,
      toDate: convertedDuration.to,
      model: prevSearchCache.selectedModels.map(m => m.modelCode),
      doNumber: prevSearchCache.doNumber,
      division: prevSearchCache.selectedDivisions.map(d => d.code).join("^"),
    };

    SellInInvoiceVsImeiScanCountModel.exportSummaryExcel(config)
      .catch(err => {
        this.handleError(err);
      })
      .finally(() => {
        isDownloadingSummary = false;
      })

  }

  const handleClickDownloadRawData = (e) => {
    e.preventDefault();
    isDownloadingRawData = true;

    const convertedDuration = convertDuration(prevSearchCache.duration);

    const config = {
      dateType: convertedDuration.type,
      fromDate: convertedDuration.from,
      toDate: convertedDuration.to,
      model: prevSearchCache.selectedModels.map(m => m.modelCode),
      doNumber: prevSearchCache.doNumber,
      division: prevSearchCache.selectedDivisions.map(d => d.code).join("^"),
    };

    SellInInvoiceVsImeiScanCountModel.exportRawData(config)
      .catch(err => {
        this.handleError(err);
      })
      .finally(() => {
        isDownloadingRawData = false;
      })

  }

  const handleSelectDivisions = (divisions = []) => {
    selectedDivisions = divisions;
  };

  const handleChangeDoNumber = (e) => {
    doNumber = e.target.value;
  };

  const fetchInitData = () => {
    this.showLoading();

    SellInInvoiceVsImeiScanCountModel.getDivisions()
      .then((divisionsRes) => {
        divisions = divisionsRes || [];
      })
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const search = () => {
    this.showLoading();

    const convertedDuration = convertDuration(duration);

    const criteria = {
      dateType: convertedDuration.type,
      fromDate: convertedDuration.from,
      toDate: convertedDuration.to,
      model: selectedModels.map(m => m.modelCode),
      doNumber,
      division: selectedDivisions.map(d => d.code).join("^"),
    };

    SellInInvoiceVsImeiScanCountModel.search(criteria)
      .then((res) => {
        searchResult = res || [];
        prevSearchCache.updateCache();
      })
      .catch((err) => {
        this.handleError(err);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const handleSubmitSearch = (e) => {
    e.preventDefault();
    e.target.classList.add("was-validated");

    if (!e.target.checkValidity()) {
      e.stopPropagation();
      return;
    }

    search();
  };

  const handleClickClear = () => {
    duration = {
      type: DATE_TYPE_CODES.DATE,
      from: moment().format(formats.dateFormat),
      to: moment().format(formats.dateFormat)
    }
    selectedModels = [];
    selectedDivisions = [];
    doNumber = "";
  };

  const handleChangeDuration = (type, from, to) => {
    duration = {
      type,
      from,
      to,
    };
  };

  const convertDuration = (duration) => {
    const DURATION_TYPES = {
      DATE: "day",
      WEEK: "week",
      MONTH: "month",
    };

    switch (duration.type) {
      case DATE_TYPE_CODES.DATE:
        return {
          type: DURATION_TYPES.DATE,
          from: moment(duration.from).format(formats.apiDateFormat),
          to: moment(duration.to).format(formats.apiDateFormat),
        };
      case DATE_TYPE_CODES.WEEK:
        return {
          type: DURATION_TYPES.WEEK,
          from: moment(duration.from).format(formats.API_WEEK_FORMAT),
          to: moment(duration.to).format(formats.API_WEEK_FORMAT),
        };
      case DATE_TYPE_CODES.MONTH:
        return {
          type: DURATION_TYPES.MONTH,
          from: moment(duration.from).format(formats.API_MONTH_FORMAT),
          to: moment(duration.to).format(formats.API_MONTH_FORMAT),
        };
      default:
        return duration;
    }
  };

  function convertData(searchResult) {
    if (!searchResult) {
      return;
    }

    const monthsNormalized = {};
    const divisionGroupsNormalized = {};

    for (let item of searchResult) {
      mutateDivisionGroupsNormalized(divisionGroupsNormalized, item);
      mutateMonthsNormalized(monthsNormalized, item);
    }

    const monthsASC = getMonthUniqueASC(monthsNormalized);
    const weeksASC = getWeeksUniqueASC(monthsASC, monthsNormalized);

    return { monthsNormalized, divisionGroupsNormalized, monthsASC, weeksASC };

    function mutateDivisionGroupsNormalized(dGroups, item) {
      const { divisionGroupName, divisionName, month, week } = item;

      if (!dGroups[divisionGroupName]) {
        dGroups[divisionGroupName] = {
          [divisionName]: {
            [month]: { [week]: item },
          },
        };
      } else {
        if (!dGroups[divisionGroupName][divisionName]) {
          dGroups[divisionGroupName][divisionName] = {
            [month]: { [week]: item },
          };
        } else {
          if (!dGroups[divisionGroupName][divisionName][month]) {
            dGroups[divisionGroupName][divisionName][month] = {
              [week]: item,
            };
          } else {
            dGroups[divisionGroupName][divisionName][month][week] = item;
          }
        }
      }
    }

    function mutateMonthsNormalized(monthsNormalized, item) {
      const { month, week } = item;

      if (!monthsNormalized[month]) {
        monthsNormalized[month] = {
          [week]: [item],
        };
      } else {
        if (!monthsNormalized[month][week]) {
          monthsNormalized[month][week] = [item];
        } else {
          monthsNormalized[month][week].push(item);
        }
      }
    }

    function getMonthUniqueASC(monthsGroup) {
      return Object.keys(monthsGroup).sort((a, b) => a.localeCompare(b));
    }

    function getWeeksUniqueASC(monthsASC, monthsNormalized) {
      const weeksUniqueASC = [];

      for (let month of monthsASC) {
        const weeksOfMonth = Object.keys(monthsNormalized[month]).sort((a, b) =>
          a.localeCompare(b)
        );

        const weeksOfMonthUniqueASCWithTotal = [...weeksOfMonth, "Total"].map(
          (week) => ({ week, month })
        );

        weeksUniqueASC.push(...weeksOfMonthUniqueASCWithTotal);
      }

      return weeksUniqueASC;
    }
  }

  return {
    oninit: () => {
      fetchInitData();
    },

    view: () => {
      return [
        m('.d-flex.justify-content-end.mb-2',
          m('a.btn.btn-primary[data-bs-toggle="collapse"][href="#search-section"][aria-controls="search-section"][aria-expanded="true"]',
            m('i.bi.bi-filter'),
            this.getText("LT00000232", "Filter"),
          ),
        ),

        m('#search-section.collapse.show',
          m(".card",
            m(".card-body",
              m("form.row.g-2.needs-validation[novalidate].align-items-end", { onsubmit: handleSubmitSearch },
                m(".col-md-6.col-lg-3",
                  m(DynamicDateComponent, {
                    selectedDateType: duration.type,
                    supportedDates: [DATE_TYPE_CODES.DATE, DATE_TYPE_CODES.WEEK, DATE_TYPE_CODES.MONTH],
                    fromDate: duration.from,
                    toDate: duration.to,
                    required: true,
                    label: this.getText("LT00001126", "Duration"),
                    onChange: handleChangeDuration
                  })
                ),

                m(".col-md-6.col-lg-3.position-relative",
                  m('label.form-label[for="model"]', this.getText("LT00000717", "Model")),
                  m(SelectModelSearchInputComponent, {
                    selectedItems: selectedModels,
                    multiple: true,
                    onSelect: handleSelectModels,
                  })
                ),

                m(".col-md-6.col-lg-3.position-relative",
                  m('label.form-label[for="divisions"]', this.getText("LT00001184", "Divisions")),
                  m(MultiSelectComponent, {
                    id: "divisions",
                    items: divisions,
                    selectedItems: selectedDivisions,
                    filterable: true,
                    itemKey: 'code',
                    itemValue: 'name',
                    onSelect: handleSelectDivisions,
                  }),
                ),

                m(".col-md-6.col-lg-3.position-relative",
                  m("label.form-label[for='do-number']", this.getText("LT00001185", "Do number")),
                  m('input#do-number.form-control.form-control-bg[maxLength="10"]', {
                    placeholder: this.getText("LT00001185", "Do number"),
                    value: doNumber,
                    onchange: handleChangeDoNumber,
                  })
                ),

                m(".row.g-2.justify-content-end.mt-2.mt-md-3",
                  m(".col-12.gap-2.d-flex.justify-content-end.flex-wrap",
                    m("button.btn.btn-primary[type=submit]",
                      this.getText("LT00000001", "Search")
                    ),

                    m("button.btn.btn-white[type=button]",
                      { onclick: handleClickClear },
                      this.getText("LT00000031", "Clear")
                    )
                  )
                )
              ),
            ),
          ),
        ),

        searchResult && m('.card.mt-3',
          m('.card-body',
            searchResult?.length > 0 &&
            m('div',
              m('.d-flex.justify-content-end.mb-2',
                m('.input-group.w-auto',
                  m('button.btn.btn-primary.dropdown-toggle.dropdown-toggle-split[type=button][data-bs-toggle=dropdown][arie-expanded=false]',
                    {
                      disabled: !searchResult,
                    },
                    isDownloadingRawData || isDownloadingSummary
                      ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                      : m('i.bi.bi-file-earmark-arrow-down-fill'),
                    this.getText("LT00000018", "Excel Download"),
                  ),
                  m('ul.dropdown-menu',
                    m('li',
                      m('a.dropdown-item[href="javascript:void(0);"]', {
                        class: isDownloadingSummary ? 'disabled' : '',
                        onclick: handleClickDownloadSummary
                      },
                        isDownloadingSummary && m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                        this.getText("LT00001095", "Summary")
                      )
                    ),
                    m('li',
                      m('a.dropdown-item[href="javascript:void(0);"]', {
                        class: isDownloadingRawData ? 'disabled' : '',
                        onclick: handleClickDownloadRawData
                      },
                        isDownloadingRawData &&
                        m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                        this.getText("LT00008050", "Raw Data")
                      )
                    ),
                  ),
                ),
              ),

              m(SellInInvoiceVsScanCountTableView, { tableData: convertData(searchResult) })

            ),

            searchResult?.length === 0 && this.getText('LT00000363', 'No items available')

          ),
        ),

        this.getBaseViews(),
      ]
    }
  }
}